import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function CalderdaleCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Calderdale Pothole Claims | B/C Roads RED Doubled in 2024 | Fixtyer"
        description="Calderdale Council's own report shows B and C roads in RED condition doubled to 10.6% in 2024, 34% of U-roads were RED in 2023, and 9,226 potholes were filled in 2023/24. See what the DfT AMBER rating means for your claim."
        canonical="/councils/calderdale"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Calderdale', url: '/councils/calderdale' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Calderdale has a GREEN spend scorecard — can I still claim?',
            answer: 'Yes. The DfT Spend rating is GREEN because Calderdale matches its DfT capital allocation and is shifting spend towards preventative maintenance — up from 62% to a projected 69% in 2025/26. But the overall rating is AMBER because road condition is declining: B and C roads in RED condition doubled to 10.6% in 2024, and U-roads hit 34% RED in 2023. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 861km — 76% of Calderdale\'s 1,130km road network — and the council itself calls them "our greatest area of concern." At the last survey, 25% were in RED condition; the peak was 34% in 2023. The council admits U-roads "are more likely to receive reactive patching unless prioritised through condition trends or safety risk" — exactly the reactive-only maintenance pattern that weakens a Section 58 defence.',
          },
          {
            question: 'Does Calderdale\'s Pennine terrain affect my pothole claim?',
            answer: 'It can strengthen it. Calderdale\'s own report states the borough is "extremely susceptible to landslides, scour, and infrastructure failure" due to topography, geology, and vulnerability to adverse weather. The council acknowledges that extreme weather, drainage failure, and traffic loading drive reactive pothole repairs. That is documented knowledge of elevated deterioration risk — which raises the bar for what a "reasonable" inspection regime must deliver on affected routes.',
          },
          {
            question: 'Pothole repairs fell from 9,226 to 6,630 in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. The 2023/24 figure of 9,226 potholes filled was the five-year peak — 86% higher than 2021/22. The drop to 6,630 in 2024/25 does not reverse the underlying condition data: B and C roads in RED condition still doubled to 10.6% in 2024, and the council still attributes a "significant proportion of reactive spend" to pothole and surface defect repairs driven by weathering and ageing surfaces.',
          },
          {
            question: 'Why is Calderdale AMBER for best practice as well as condition?',
            answer: 'The DfT Best Practice scorecard is AMBER partly because Calderdale\'s Highway Infrastructure Asset Management Plan is currently being fully reviewed and rewritten, and the council is mid-transition to PAS 2161 compliant AI-based condition surveys. Until the updated HIAMP is published and the new survey methodology is fully embedded, the council\'s asset management documentation is incomplete — which matters when it tries to rely on a systematic Section 58 defence.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Calderdale Council"
        councilSlug="calderdale"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/calderdale"
        headline="Calderdale Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Calderdale</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Calderdale: Pennine Roads, Doubling RED B/C Routes
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Calderdale matches its DfT capital allocation and earns a <strong className="text-emerald-400">GREEN</strong> spend
            scorecard. Yet the overall rating is <strong className="text-amber-400">AMBER</strong> — because B and C roads in
            RED condition <strong className="text-rose-400">doubled to 10.6% in 2024</strong>, one in three U-roads was RED
            at the 2023 peak, and the council filled <strong className="text-rose-400">9,226 potholes in 2023/24 alone</strong> across
            a Pennine network it admits is uniquely vulnerable to weather and landslides.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">10.6%</div>
            <div className="text-white font-medium mt-2">B and C roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 5.3% in 2023 — a doubling in twelve months across Calderdale's 120km of classified
              local roads, while 861km of U-roads remain the council's "greatest area of concern."
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Calderdale's own transparency report — A-roads slipping back,
            B and C roads deteriorating sharply, U-roads persistently poor
          </p>

          {/* Network size */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">Network size (km)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">149</div>
                <div className="text-sm text-surface-600">A-roads</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">120</div>
                <div className="text-sm text-surface-600">B and C roads</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">861</div>
                <div className="text-sm text-surface-600">U-roads</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-900">1,130</div>
                <div className="text-sm text-surface-600">Total roads</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm mt-4">
              U-roads account for 76% of Calderdale's road network — the roads where residents start and
              end almost every journey, per the council's own report.
            </p>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (149km — 13.2% of network): slipping after improvement</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">75%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3.8%</td>
                    <td className="px-4 py-2 text-center">22.5%</td>
                    <td className="px-4 py-2 text-center">73.7%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center text-emerald-600">1.9%</td>
                    <td className="px-4 py-2 text-center">17.4%</td>
                    <td className="px-4 py-2 text-center">80.7%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">2.3%</td>
                    <td className="px-4 py-2 text-center">17.5%</td>
                    <td className="px-4 py-2 text-center">80.2%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5.1%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">25.4%</td>
                    <td className="px-4 py-2 text-center font-bold">69.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-roads improved through 2023, then RED condition more than doubled to 5.1% in 2024 and
              green-condition roads fell from 80.2% to 69.5%. Even the best-maintained tier is reversing.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (120km — 10.6% of network): RED condition doubled</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4.8%</td>
                    <td className="px-4 py-2 text-center">21.2%</td>
                    <td className="px-4 py-2 text-center">68%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">7.5%</td>
                    <td className="px-4 py-2 text-center">30.2%</td>
                    <td className="px-4 py-2 text-center">62.3%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">5.5%</td>
                    <td className="px-4 py-2 text-center">29.4%</td>
                    <td className="px-4 py-2 text-center">65.1%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5.3%</td>
                    <td className="px-4 py-2 text-center">27.8%</td>
                    <td className="px-4 py-2 text-center">66.9%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">10.6%</td>
                    <td className="px-4 py-2 text-center font-bold">31%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">58.4%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition B and C roads doubled from 5.3% to 10.6% in a single year. Good-condition
              roads fell from 66.9% to 58.4% — meaning over 40% of Calderdale's classified local roads now
              need or will soon need maintenance.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£8.5m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26 (projected)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£8.5m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">69%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share (2025/26)</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Calderdale spends its full DfT allocation and is shifting towards preventative maintenance —
              up from 62% in 2022/23 to a projected 69% in 2025/26. Yet B and C roads in RED condition
              still doubled in 2024. The problem is not under-spending at the macro level. The network is
              deteriorating faster than even a GREEN-rated spend programme can hold it.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Problem */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 861km U-Road Problem
          </h2>
          <p className="text-surface-600 mb-8">
            76% of Calderdale's network is unclassified roads — and the council names them its greatest concern
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">27%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">26%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">21%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">34%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">25%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Due to historic underinvestment and increasing wear from traffic and weather, U-roads represent our greatest area of concern, with condition trends indicating a slow but steady deterioration over recent years."
              source="Calderdale Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="While classified roads benefit from SCANNER data and larger resurfacing schemes, U-roads are more likely to receive reactive patching unless prioritised through condition trends or safety risk."
              source="Calderdale Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">CVI Surveys, Not SCANNER</h3>
              <p className="text-surface-600 text-sm mb-3">
                Calderdale uses SCANNER surveys for A, B and C roads, but unclassified roads are assessed
                via CVI (Condition Visual Inspection). The council is exploring PAS 2161 compliant AI-based
                surveys — but that transition is not yet complete.
              </p>
              <p className="text-surface-600 text-sm">
                At the 2023 peak, <strong>34% of U-roads were in RED condition</strong> — roughly 293km of
                residential streets, estate roads and rural lanes across the Calder Valley.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Reactive Patching by Default</h3>
              <p className="text-amber-800 text-sm mb-3">
                The council's own words: U-roads get reactive patching unless specifically prioritised.
                That is precisely the maintenance pattern that produces potholes between inspections —
                and weakens a Section 58 defence on the roads where most Calderdale residents actually drive.
              </p>
              <p className="text-amber-800 text-sm">
                Almost every journey begins and ends on a U-road. The council says as much in its report.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for
              knowing the condition of its roads. For Calderdale's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road surveyed by CVI — and when was the last assessment?</li>
              <li>• If 34% of U-roads were RED at the 2023 peak, what was done about yours?</li>
              <li>• Did the council only react with patching after you reported the defect?</li>
              <li>• Has the PAS 2161 transition changed how your road's condition is recorded?</li>
            </ul>
            <p className="text-white font-medium">
              A council that admits U-roads are its "greatest area of concern" yet defaults to reactive
              patching cannot claim systematic knowledge of the network most residents use daily.
            </p>
          </div>
        </div>
      </section>

      {/* The Pennine Terrain Admission */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Pennine Terrain Admission
          </h2>
          <p className="text-surface-600 mb-8">
            Calderdale's own explanation for accelerated deterioration — geology, weather, and landslides
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Calderdale is extremely susceptible to landslides, scour, and infrastructure failure. There are a number of reasons for this including our topography, geology, vulnerability to adverse weather and extensive asset base."
              source="Calderdale Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Landslides are very common all along the steep slopes of the Calder valley and its tributary valleys which have alternating sandstones and mudstones strata. This is inherently an unstable situation, which is made worse by the fact that many of the valley slopes in this area are already very steep."
              source="Calderdale Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="A significant proportion of reactive spend is attributed to pothole and surface defect repairs, which are often required due to weathering, ageing surfaces, and traffic loading."
              source="Calderdale Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Admission Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Calderdale formally acknowledges that its topography, geology, and vulnerability to adverse
                weather make infrastructure failure common. Extreme weather, drainage asset failure, and
                traffic loading are documented drivers of pothole formation.
              </p>
              <p className="text-surface-600 text-sm">
                The council also states it is "unique amongst Local authorities" in combining challenging
                topography with relatively high population density. That is documented knowledge of elevated
                deterioration risk on specific routes.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your road on a steep valley slope or flood-affected route?</li>
                <li>• Did the council increase inspection frequency after known weather events?</li>
                <li>• If deterioration was known to be accelerated by geology and weather, why wasn't the defect caught?</li>
                <li>• Were drainage assets on your route inspected before the surface failed?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            32,520 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many potholes this network produces
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">4,967</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/22*</td>
                  <td className="px-6 py-4 text-center">5,202</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">6,495</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">9,226</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">6,630</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">32,520</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">9,226 in a Single Year</h3>
              <p className="text-surface-600 text-sm">
                2023/24 was the five-year peak — 86% more potholes filled than 2021/22. That is roughly
                25 pothole repairs every day across a 1,130km network. A road producing defects at that
                rate is, by definition, a road where potholes routinely form between inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">*Publishing Error in the Report</h3>
              <p className="text-amber-800 text-sm">
                The council's published table labels the second column "2022/22" — an apparent typographical
                error in the transparency report PDF. The figure of 5,202 and all other values are reproduced
                exactly as published. The five-year total of 32,520 is derived by summing those published figures.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending detail */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Where The Money Goes
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of capital and revenue spend from Calderdale's transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">6,343</td>
                  <td className="px-4 py-3 text-center">6,343</td>
                  <td className="px-4 py-3 text-center">3,611</td>
                  <td className="px-4 py-3 text-center">64%</td>
                  <td className="px-4 py-3 text-center">36%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">4,921</td>
                  <td className="px-4 py-3 text-center">6,159</td>
                  <td className="px-4 py-3 text-center">3,612</td>
                  <td className="px-4 py-3 text-center">63%</td>
                  <td className="px-4 py-3 text-center">37%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">6,197</td>
                  <td className="px-4 py-3 text-center">6,197</td>
                  <td className="px-4 py-3 text-center">3,733</td>
                  <td className="px-4 py-3 text-center">62%</td>
                  <td className="px-4 py-3 text-center">38%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">7,257</td>
                  <td className="px-4 py-3 text-center">7,257</td>
                  <td className="px-4 py-3 text-center">3,733</td>
                  <td className="px-4 py-3 text-center">66%</td>
                  <td className="px-4 py-3 text-center">34%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">7,769</td>
                  <td className="px-4 py-3 text-center">7,769</td>
                  <td className="px-4 py-3 text-center">3,733</td>
                  <td className="px-4 py-3 text-center">68%</td>
                  <td className="px-4 py-3 text-center">32%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26*</td>
                  <td className="px-4 py-3 text-center font-bold">8,477</td>
                  <td className="px-4 py-3 text-center font-bold">8,477</td>
                  <td className="px-4 py-3 text-center font-bold">3,733</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">69%</td>
                  <td className="px-4 py-3 text-center font-bold">31%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-500 text-sm italic">
            *2025/26 figures are projected. Capital spend figures rounded to nearest £1,000 from published values.
            Revenue spend has been flat at £3,733,000 since 2022/23.
          </p>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Calderdale is not Derbyshire — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — matches DfT capital allocation in full
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Preventative share rising — from 62% to a projected 69%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management strategy with SCANNER surveys on classified roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> All-Street Permit Scheme for coordinated streetworks
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Transition to PAS 2161 AI-based surveys underway
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on A-roads and key routes. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — B and C roads in RED doubled to 10.6% in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 861km of U-roads (76% of network) — council's own "greatest area of concern"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 34% of U-roads RED at the 2023 peak — roughly one in three
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 32,520 potholes filled in five years — defects form faster than inspections catch them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-roads default to reactive patching, not planned resurfacing
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Admitted vulnerability to landslides, scour, and adverse weather across the Calder Valley
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — HIAMP currently being fully reviewed and rewritten
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a rising preventative share, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the reactive-patching admission is your strongest structural argument</li>
              <li>• Location on steep valley slopes, flood-affected routes, or post-weather-event timelines</li>
              <li>• Whether the HIAMP rewrite means the council's published inspection regime covered your road at the time</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Calderdale's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Calderdale?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road reactive-patching argument</li>
                <li>• No prior-report search</li>
                <li>• No Pennine terrain analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C road RED doubling documented</li>
                <li>✅ U-road "greatest concern" admission cited</li>
                <li>✅ 32,520 potholes in five years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Calderdale</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=calderdale"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Calderdale Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Calderdale has a GREEN spend scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend rating is GREEN because Calderdale matches its DfT capital allocation and
                is shifting spend towards preventative maintenance — up from 62% to a projected 69% in 2025/26.
                But the overall rating is AMBER because road condition is declining: B and C roads in RED
                condition doubled to 10.6% in 2024, and U-roads hit 34% RED in 2023. Section 58 turns on
                whether the specific defect that damaged your vehicle was reasonably inspected and repaired,
                not on aggregate spend.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 861km — 76% of Calderdale's 1,130km road network — and the council itself
                calls them "our greatest area of concern." At the last survey, 25% were in RED condition;
                the peak was 34% in 2023. The council admits U-roads "are more likely to receive reactive
                patching unless prioritised through condition trends or safety risk" — exactly the reactive-only
                maintenance pattern that weakens a Section 58 defence.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Calderdale's Pennine terrain affect my pothole claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can strengthen it. Calderdale's own report states the borough is "extremely susceptible
                to landslides, scour, and infrastructure failure" due to topography, geology, and vulnerability
                to adverse weather. The council acknowledges that extreme weather, drainage failure, and traffic
                loading drive reactive pothole repairs. That is documented knowledge of elevated deterioration
                risk — which raises the bar for what a "reasonable" inspection regime must deliver on affected routes.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs fell from 9,226 to 6,630 in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The 2023/24 figure of 9,226 potholes filled was the five-year peak — 86% higher than
                2021/22. The drop to 6,630 in 2024/25 does not reverse the underlying condition data: B and C
                roads in RED condition still doubled to 10.6% in 2024, and the council still attributes a
                "significant proportion of reactive spend" to pothole and surface defect repairs driven by
                weathering and ageing surfaces.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is Calderdale AMBER for best practice as well as condition?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT Best Practice scorecard is AMBER partly because Calderdale's Highway Infrastructure
                Asset Management Plan is currently being fully reviewed and rewritten, and the council is
                mid-transition to PAS 2161 compliant AI-based condition surveys. Until the updated HIAMP is
                published and the new survey methodology is fully embedded, the council's asset management
                documentation is incomplete — which matters when it tries to rely on a systematic Section 58 defence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a
              href="https://new.calderdale.gov.uk/streets-and-transport/roads-and-highways-maintenance/hiamp"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Calderdale Council Local Highways Maintenance Transparency Report (June 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
